import { useState } from 'react';
import { Alert, Button, Card, ProofSeal } from '@argink/ui';
import { downloadSigned, validateSignedEnvelope, type SignedValidationView } from '../lib/api';
import { describeError, type HumanError } from '../lib/errors';
import { SupportCode } from './SupportCode';

export interface SuccessPanelProps {
  title: string;
  downloadPath: string;
  fileHint: string;
  envelopeCompleted?: boolean;
  /** Envelope da cerimônia — habilita a ação "Validar documento assinado" (D-063/#100). */
  envelopeId: string;
}

// D-063/#100 — mesmo desenho honesto do owner (apps/web copy.ts): `indeterminate`
// NUNCA vira "inválido" e nenhuma variante promete aprovação de autoridade.
const VALIDATE_RESULT_COPY: Record<SignedValidationView['result'], { title: string; detail: string }> = {
  valid: {
    title: 'Assinaturas do PDF validadas',
    detail:
      'As assinaturas digitais do documento foram verificadas com sucesso pelo motor de validação: ' +
      'a integridade está preservada e a cadeia de certificação foi confirmada.',
  },
  invalid: {
    title: 'A validação reprovou o documento',
    detail:
      'O motor de validação encontrou um problema nas assinaturas deste PDF. Avise quem enviou o ' +
      'documento antes de usá-lo como prova.',
  },
  indeterminate: {
    title: 'Não foi possível confirmar toda a cadeia',
    detail:
      'A assinatura confere com o documento, mas o motor não conseguiu confirmar toda a cadeia de ' +
      'certificação agora. Isto NÃO significa que o documento é inválido — tente novamente mais ' +
      'tarde ou confira no VALIDAR do ITI (validar.iti.gov.br).',
  },
};

// R1b/P1-2 (mesma fuga semântica do owner, superfície do signatário): indicação INTERNAL_SEAL_* é o
// selo interno da plataforma (D-069) — NUNCA "cadeia de certificação confirmada".
const VALIDATE_RESULT_COPY_INTERNAL_SEAL: Partial<Record<SignedValidationView['result'], { title: string; detail: string }>> = {
  valid: {
    title: 'Selo interno da plataforma validado',
    detail:
      'O selo interno do ARGINK foi verificado com sucesso pelo motor de validação: a integridade do ' +
      'documento está preservada. Este selo é emitido sob uma raiz privada da plataforma e não ' +
      'substitui uma assinatura com certificado digital do signatário.',
  },
  invalid: {
    title: 'A validação reprovou o selo interno',
    detail:
      'O motor de validação encontrou um problema no selo interno deste PDF. Avise quem enviou o ' +
      'documento antes de usá-lo como prova.',
  },
};

/** Copy por REGIME da indicação técnica: selo interno × ICP. */
export function resultCopy(v: Pick<SignedValidationView, 'result' | 'indication'>): { title: string; detail: string } {
  return (v.indication.startsWith('INTERNAL_SEAL') && VALIDATE_RESULT_COPY_INTERNAL_SEAL[v.result]) || VALIDATE_RESULT_COPY[v.result];
}

const VALIDATE_EXPLANATION =
  'Validar o documento confere as assinaturas digitais dentro do próprio PDF assinado — é diferente ' +
  'do pacote de evidências da cerimônia, que registra quem assinou, quando e por qual canal.';

/** Tela de conclusão do signatário — o único lugar em que o Proof Lime do
 * `ProofSeal` faz sentido nesta jornada: é literalmente o evento de prova/
 * conclusão que o brandbook reserva a essa cor (BRAND-EXTRACT §4/§7). */
export function SuccessPanel({ title, downloadPath, fileHint, envelopeCompleted, envelopeId }: SuccessPanelProps) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<HumanError | null>(null);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<SignedValidationView | null>(null);
  const [validationError, setValidationError] = useState<HumanError | null>(null);

  async function handleDownload() {
    setDownloading(true);
    setError(null);
    try {
      // "Baixar" é operação de negócio própria (item 1 da rodada) — downloadSigned
      // gera seu próprio trace_id (1 por clique) quando não recebe um explícito.
      const blob = await downloadSigned(downloadPath);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileHint;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setDownloading(false);
    }
  }

  async function handleValidate() {
    setValidating(true);
    setValidationError(null);
    try {
      setValidation(await validateSignedEnvelope(envelopeId));
    } catch (err) {
      setValidationError(describeError(err));
    } finally {
      setValidating(false);
    }
  }

  return (
    <Card className="success-panel">
      <ProofSeal label={title} size={40} />
      <p className="card__note">
        {/* Dois defeitos numa linha, achados pela auditoria adversarial do M6: "todos os
            signatários concluíram" é falso num envio concluído por quorum (M de N), e
            "envelope" é a palavra do CONTRATO na tela do signatário — a oitava ocorrência,
            escondida do gate porque a amostra do teste nunca passava `envelopeCompleted`. */}
        {envelopeCompleted
          ? 'O envio foi concluído — o documento final está disponível.'
          : 'Sua parte está concluída. Aguardando os demais signatários, se houver.'}
      </p>
      <Button variant="primary" onClick={() => void handleDownload()} disabled={downloading} isLoading={downloading}>
        Baixar documento assinado
      </Button>
      {envelopeCompleted ? (
        <Button variant="outline" onClick={() => void handleValidate()} disabled={validating} isLoading={validating}>
          Validar documento assinado
        </Button>
      ) : (
        <p className="card__note">
          {/* Não "quando todos concluírem" (P1-1, 2ª passada): sob `parallel_quorum` o envio
              conclui — e a validação abre — com M de N. O gatilho é a conclusão do ENVIO, que
              é verdade nos dois modos; "todos" era falso exatamente no modo em que a frase
              aparece por mais tempo. */}
          A validação do documento assinado fica disponível quando o envio for concluído.
        </p>
      )}
      {error ? (
        <Alert tone="danger" title={error.title}>
          <p>{error.detail}</p>
          <SupportCode traceId={error.traceId} />
        </Alert>
      ) : null}
      {validationError ? (
        <Alert tone="danger" title={validationError.title}>
          <p>{validationError.detail}</p>
          <SupportCode traceId={validationError.traceId} />
        </Alert>
      ) : null}
      {validation ? (
        <Alert
          tone={validation.result === 'valid' ? 'proof' : validation.result === 'invalid' ? 'danger' : 'info'}
          title={resultCopy(validation).title}
        >
          <p>{resultCopy(validation).detail}</p>
          <p className="card__note">
            Resultado técnico: {validation.indication}
            {validation.sub_indication ? ` / ${validation.sub_indication}` : ''} ·{' '}
            {validation.signature_count} assinatura{validation.signature_count === 1 ? '' : 's'}
          </p>
          <p>{VALIDATE_EXPLANATION}</p>
        </Alert>
      ) : null}
    </Card>
  );
}
